/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Col, Container, Row } from '@pds-react/grid';
import '@pds-react/grid/dist/grid.min.css';
import Input from '@pds-react/input';
import '@pds-react/input/dist/input.min.css';
import '@pds-react/input/dist/erroredInput.min.css';
import '@pds-react/input/dist/helperInput.min.css';
import '@pds-react/input/dist/labeledInput.min.css';
import Button from '@pds-react/button';
import '@pds-react/button/dist/button.min.css';
import React, { useContext, useState } from 'react';
import { AppPageProps, AppState, LocalizationContext, SessionId } from '../app';
import Alert from '@pds-react/alert';
import '@pds-react/alert/dist/alert.min.css';
import AnimatedIcon from '@pds-react/animatedIcon';
import '@pds-react/animatedIcon/dist/animatedIcon.min.css';
import { v4 as uuid } from 'uuid';

const EnterIdNumber: React.FunctionComponent<AppPageProps> = (props: AppPageProps) => {
  const [crdNumber, setCrdNumber] = useState('');
  const [crdValidationMessage, setCrdValidationMessage] = useState('');
  const [error, setError] = useState({
    errorMessage: '',
    fieldId: '',
    linkText: '',
  });
  const [slowRequest, setSlowRequest] = useState(false);
  const [loading, setLoading] = useState(false);

  const localization = useContext(LocalizationContext)!;
  const sessionId = useContext(SessionId);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    setCrdValidationMessage('');
    setError({ errorMessage: '', fieldId: '', linkText: '' });

    if (crdNumber.length === 0) {
      setCrdValidationMessage(localization.t('enter-crd:error-crd-empty'));
      return;
    }

    setLoading(true);

    const slowRequestMessage = setTimeout(() => {
      setSlowRequest(true);
    }, 2000);

    const timeoutController = new AbortController();
    const apiTimeout = setTimeout(() => {
      timeoutController.abort();
    }, 10000);

    const resetState = (): void => {
      clearTimeout(slowRequestMessage);
      clearTimeout(apiTimeout);
      setLoading(false);
      setSlowRequest(false);
    };

    fetch(`${process.env.REACT_APP_CRD_API}/${encodeURIComponent(crdNumber)}`, {
      method: 'POST',
      headers: {
        'X-Session-Id': sessionId,
        'X-Transaction-Id': uuid(),
      },
      signal: timeoutController.signal,
    })
      .then((response) => {
        resetState();
        if (response.status === 204) {
          props.changeState(AppState.EMAIL_SENT);
        } else if (response.status === 404) {
          setError({
            errorMessage: 'enter-crd:crd-not-found',
            fieldId: 'crd',
            linkText: 'enter-crd:crd-input-label',
          });
        } else if (response.status === 409) {
          props.changeState(AppState.BUILT_NOT_ACTIVATED);
        } else {
          props.changeState(AppState.CONTACT_SUPPORT);
        }
      })
      .catch(() => {
        resetState();
        props.changeState(AppState.CONTACT_SUPPORT);
      });
  };

  return (
    <>
      <Container style={{ marginTop: '40px', marginBottom: '40px' }}>
        <Row>
          {error.errorMessage !== '' && (
            <Col span={12} md={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
              <Alert variant="error" role="alert">
                {localization.t(error.errorMessage)}{' '}
                <a href={'#' + error.fieldId} className="pds-link">
                  {localization.t(error.linkText)}
                </a>
              </Alert>
              <br />
            </Col>
          )}
          {slowRequest && (
            <Col span={12} md={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
              <Alert variant="information" role="alert">
                {localization.t('misc:slow-request')}
              </Alert>
              <br />
            </Col>
          )}
          <Col span={12} sm={{ span: 10, offset: 1 }} md={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
            <div className="pds-typography-center">
              <h1 className="pds-typography-h2">{localization.t('enter-crd:page-title')}</h1>
            </div>
            <br />
            <div className="pds-typography-center">
              <p>{localization.t('enter-crd:page-description')}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <Input
                id="crd"
                label={localization.t('enter-crd:crd-input-label')}
                name="crd"
                required={true}
                value={crdNumber}
                onChange={(e) => setCrdNumber(e.target.value)}
                errorMessage={crdValidationMessage}
              />
              <br />
              <div className="pds-typography-center">
                <Button style={{ minWidth: '200px' }} aria-disabled={loading}>
                  <span className="pds-typography-center" style={{ width: '100%' }}>
                    {!loading && localization.t('misc:submit-button')}
                    {loading && (
                      <>
                        <AnimatedIcon animationType="rotate" aria-label="loading" name="loader" />
                        <span className="sr-only" aria-live="assertive">
                          {localization.t('misc:loading')}
                        </span>
                      </>
                    )}
                  </span>
                </Button>
              </div>
            </form>
            <br />
            <div style={{ borderTop: '1px solid #C7C7C7', paddingBottom: '20px' }} />
            <div style={{ textAlign: 'center' }}>
              {localization.t('enter-crd:returning-user')}{' '}
              <a href={process.env.REACT_APP_PRINCIPALAM_LOGIN_URL}>{localization.t('enter-crd:sign-in')}</a>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EnterIdNumber;
